<template>
    <div>
        <production-status :state="state" />
        <label>Tryckformat (låses med korret)</label>
        <select v-model="printFormat" class="form-control mb-2" :disabled="source !== 'manual_image' || loading || exporting" @change="render">
            <option :value="null">Välj tryckformat</option>
            <optgroup v-if="seriesFormats.length" label="Kortserier på ordern">
                <option v-for="format in seriesFormats" :key="format.id" :value="format.id">{{ format.label }}</option>
            </optgroup>
            <option v-if="seriesFormats.length" value="__format-divider" disabled>────────────────────</option>
            <optgroup label="Fördefinierade format">
                <option v-for="format in presetFormats" :key="format.id" :value="format.id">{{ format.label }}</option>
            </optgroup>
            <option v-if="printFormat && !printFormats.some((f) => f.id === printFormat)" :value="printFormat">Sparat designformat {{ printFormat }}</option>
        </select>
        <label>Korrbakgrund</label>
        <select v-model="selected" class="form-control mb-2" :disabled="loading || exporting" @change="render">
            <option value="">Välj korrbakgrund</option>
            <option v-for="proof in availableProofs" :key="proof.id" :value="proof.id">{{ proof.label }}</option>
        </select>
        <p class="text-muted">Spara korret på ordern. Godkänn det därefter för att skapa tryckfil.</p>
        <approve-proof-button v-if="savedProof" :order-id="orderId" :proof="savedProof" :revision="state.revision" :disabled="loading || exporting" @approved="approved" />
        <p v-if="loading || exporting" class="text-muted" role="status">Bearbetar korrektur…</p>
        <p v-if="notice" class="alert alert-success" role="status">{{ notice }}</p>
        <p v-if="error" class="alert alert-danger" role="alert">{{ error }}</p>
        <canvas v-show="ready" ref="pdf" style="display: block; width: 100%;" aria-label="Korrektur"></canvas>
    </div>
</template>

<script>
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs';
import apiClient from '@services/ApiClient';
import uploadChunks from '@services/ChunkedUpload';
import savedGreetingPreview from '@services/SavedGreetingPreview';
import ApproveProofButton from '@components/Greeting/ApproveProofButton.vue';
import ProductionStatus from '@components/Greeting/ProductionStatus.vue';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

export default {
    components: { ApproveProofButton, ProductionStatus },
    props: {
        requiredSource: { type: String, default: null },
        proofs: { type: Array, default: () => [] },
        orderId: { type: [String, Number], required: true },
    },
    data: () => ({
        state: {
            revision: 0, current: {}, versions: {}, approved: null,
        },
        source: null,
        printFormats: [],
        printFormat: null,
        draftFormat: null,
        savedProof: null,
        selected: '',
        availableProofs: [],
        error: '',
        notice: '',
        loading: false,
        ready: false,
        exporting: false,
        preview: null,
        saved: false,
    }),
    computed: {
        seriesFormats() { return this.printFormats.filter((format) => format.id.startsWith('series:')); },
        presetFormats() { return this.printFormats.filter((format) => !format.id.startsWith('series:')); },
        actionState() {
            return {
                canSave: this.ready && !this.loading && !this.exporting && !this.saved, saving: this.exporting, busy: this.loading || this.exporting, saved: this.saved,
            };
        },
    },
    watch: {
        actionState: {
            immediate: true,
            handler(state) { this.$emit('status-change', state); },
        },
    },
    async created() {
        try {
            const [formats, state] = await Promise.all([
                apiClient.get(`/vue/cardorders/${this.orderId}/production/formats`),
                apiClient.get(`/vue/cardorders/${this.orderId}/production`),
            ]);
            if (this._isDestroyed) return;
            this.state = state.data; this.availableProofs = formats.data.proofs; this.printFormats = formats.data.print;
            this.draftFormat = formats.data.draft_format;
            this.source = formats.data.proof_source;
            if (this.requiredSource && this.source !== this.requiredSource) throw new Error('Ta bort vald hälsningsbild på ordern för att skapa korr från designutkastet.');
            if (!formats.data.can_create_proof) throw new Error('Spara en design eller välj och spara en giltig manuell hälsningsbild på ordern först.');
            this.printFormat = this.source === 'draft' ? this.draftFormat : null;
            this.selected = this.availableProofs[0]?.id || '';
            await this.$nextTick();
            if (!this._isDestroyed) await this.render();
        } catch (error) { this.fail(error); }
    },
    beforeDestroy() {
        this.renderId = (this.renderId || 0) + 1;
        if (this.pdfTask) this.pdfTask.destroy().catch(() => undefined);
    },
    methods: {
        fail(error) { this.error = error.response?.data?.message || error.message || 'Korrekturet kunde inte skapas.'; },
        async render() {
            this.error = ''; this.notice = ''; this.ready = false; this.saved = false; this.savedProof = null; this.preview = null;
            if (!this.selected || !this.printFormat) return;
            this.loading = true;
            const renderId = (this.renderId || 0) + 1;
            this.renderId = renderId;
            try {
                if (this.pdfTask) await this.pdfTask.destroy();
                const url = `/vue/cardorders/${this.orderId}/production/prepare`;
                let response;
                if (this.source === 'draft') {
                    const draft = await savedGreetingPreview(this.orderId);
                    this.printFormat = draft.format;
                    response = await uploadChunks(apiClient, url, draft.blob, { version: draft.version, format: this.selected });
                } else {
                    response = await apiClient.post(url, {
                        kind: 'korr', format: this.selected, print_format: this.printFormat, revision: this.state.revision,
                    });
                }
                if (renderId !== this.renderId) return;
                this.preview = response.data;
                this.pdfTask = pdfjs.getDocument(this.preview.url);
                this.pdfDocument = await this.pdfTask.promise;
                if (renderId !== this.renderId) return;
                const page = await this.pdfDocument.getPage(1);
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = this.$refs.pdf;
                canvas.width = viewport.width; canvas.height = viewport.height;
                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                if (renderId === this.renderId) this.ready = true;
            } catch (error) { if (renderId === this.renderId) this.fail(error); } finally { if (renderId === this.renderId) this.loading = false; }
        },
        async ensureSaved() {
            if (this.saved) return;
            const response = await apiClient.post(`/vue/cardorders/${this.orderId}/production/save`, { token: this.preview.token });
            this.state = response.data; this.saved = true;
            const savedId = response.data.saved_version;
            this.savedProof = response.data.versions[savedId];
            this.notice = 'Korrekturet har sparats på ordern. Godkänn det för att skapa tryckfil.';
            this.$emit('saved', response.data);
        },
        approved(state) {
            this.state = state; this.savedProof = state.versions[this.savedProof.id];
            this.notice = 'Korret är godkänt och hälsningen används på ordern. Du kan nu skapa tryckfil.';
            this.$emit('saved', state);
        },
        async save() {
            if (!this.ready || this.loading || this.exporting || this.saved) return;
            this.exporting = true; this.error = '';
            try { await this.ensureSaved(); } catch (error) { this.fail(error); } finally { this.exporting = false; }
        },
    },
};
</script>
